import React, { useState, useRef } from 'react';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Upload, CheckCircle, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const InbodyUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('CSV 파일만 업로드 가능합니다');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('파일을 먼저 선택해주세요');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/admin/inbody/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(`업로드 완료: 신규 ${res.data.new_count}건 / 중복 ${res.data.duplicate_count}건 / 미매칭 ${res.data.unmatched_count}건`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('업로드 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">인바디 자동 업로드</h1>
        <p className="text-gray-500 text-sm mt-1">
          InBody export CSV 파일을 그대로 업로드하면 전화번호 기준으로 회원과 매칭하여 신규 측정만 누적 저장됩니다.
        </p>
      </div>

      {/* 업로드 카드 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" />
            CSV 업로드
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={handleSelect}
              data-testid="inbody-file-input"
              className="block text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
            />
            {file && (
              <span className="text-sm text-gray-600 flex items-center gap-1.5" data-testid="selected-filename">
                <FileText className="h-3.5 w-3.5" /> {file.name} ({Math.round(file.size / 1024)} KB)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              data-testid="inbody-upload-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  업로드 중...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  업로드 시작
                </>
              )}
            </Button>
            {(file || result) && (
              <Button variant="outline" onClick={handleReset} disabled={uploading}>
                초기화
              </Button>
            )}
          </div>
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-3 leading-relaxed">
            <p className="font-semibold mb-1">📌 안내</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>현재 MVP는 <span className="font-semibold">.csv</span> 형식만 지원합니다 (xlsx 미지원)</li>
              <li>매칭 기준: <span className="font-semibold">Mobile Number</span> (전화번호) — 하이픈/공백/꺾쇠 자동 정규화</li>
              <li>중복 판단: <span className="font-semibold">(회원, 측정일시)</span> 동일 시 자동 skip</li>
              <li>CRM에 등록된 회원만 저장됩니다. 미매칭 데이터는 저장되지 않습니다 (자동 회원 생성 X)</li>
              <li>저장 항목: 체중(Weight) / 골격근량(SMM) / 체지방률(PBF) / 인바디점수(InBody Score)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* 결과 카드 */}
      {result && (
        <Card data-testid="inbody-upload-result">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              업로드 결과
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 요약 그리드 */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryTile label="총 데이터" value={result.total} color="gray" testid="result-total" />
              <SummaryTile label="신규 저장" value={result.new_count} color="green" testid="result-new" />
              <SummaryTile label="중복 제외" value={result.duplicate_count} color="blue" testid="result-duplicate" />
              <SummaryTile label="회원 미매칭" value={result.unmatched_count} color="amber" testid="result-unmatched" />
              <SummaryTile label="형식 오류" value={result.invalid_count} color="red" testid="result-invalid" />
            </div>

            {/* 미매칭 상세 */}
            {result.unmatched_rows && result.unmatched_rows.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="font-semibold text-sm text-amber-800 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" />
                  회원 미매칭 ({result.unmatched_count}건) — 저장되지 않음
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-100 sticky top-0">
                      <tr>
                        <th className="p-1.5 text-left">행</th>
                        <th className="p-1.5 text-left">Mobile</th>
                        <th className="p-1.5 text-left">ID</th>
                        <th className="p-1.5 text-left">측정일시</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unmatched_rows.map((r, i) => (
                        <tr key={i} className="border-t border-amber-100">
                          <td className="p-1.5 text-gray-500">{r.row}</td>
                          <td className="p-1.5 font-mono">{r.mobile}</td>
                          <td className="p-1.5 font-mono">{r.id || '-'}</td>
                          <td className="p-1.5">{r.test_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 형식 오류 상세 */}
            {result.invalid_rows && result.invalid_rows.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="font-semibold text-sm text-red-800 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" />
                  형식 오류 ({result.invalid_count}건)
                </div>
                <div className="overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-red-100 sticky top-0">
                      <tr>
                        <th className="p-1.5 text-left">행</th>
                        <th className="p-1.5 text-left">Mobile</th>
                        <th className="p-1.5 text-left">측정일시</th>
                        <th className="p-1.5 text-left">사유</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.invalid_rows.map((r, i) => (
                        <tr key={i} className="border-t border-red-100">
                          <td className="p-1.5 text-gray-500">{r.row}</td>
                          <td className="p-1.5 font-mono">{r.mobile || '-'}</td>
                          <td className="p-1.5">{r.test_date || '-'}</td>
                          <td className="p-1.5">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, color, testid }) => {
  const colorClass = {
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  }[color] || 'bg-gray-50 border-gray-200 text-gray-800';
  return (
    <div className={`rounded-md border p-3 ${colorClass}`} data-testid={testid}>
      <div className="text-[11px] font-medium opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value ?? 0}</div>
    </div>
  );
};

export default InbodyUpload;
